// Resource.Ast — the format-agnostic semantic material content (the pivot).
//
// A material's content is ONE canonical, pure, format-free tree of
// pedagogical primitives — the interlingua every author-shape ingests INTO
// and every export folds OUT of. Before this module, `Content` (a branded
// string) carried one of four disagreeing inner shapes (pqbl-torus course
// object / freeform element array / plain text / the empty `"[]"` draft) and
// three decoders (render / create / edit) read them differently — the A-15 +
// A-18 fracture (.claude/analysis/dogfood-chat-2026-09-08/). D1 dissolves that
// by making the material store ONE shape: this AST, serialized.
//
// Pure/impure split applied to content: what the author MEANS (the
// pedagogical structure) is the pure domain; how it renders/packages is
// infrastructure. So pqbl-torus stops being a stored shape — it becomes an
// INGEST parser (world-shape → AST, run once at create) and, later, an emit
// target (D2). See .claude/analysis/directions-2026-09-08/D1-semantic-ast-pivot.md.
//
// Storage note (parse, don't validate): `Content` stays the serialized brand
// so the contentHash lock, blob store, and events-are-truth keep working on
// bytes unchanged; the AST is the PARSED in-memory type. The serialized form
// is a generic element tree (`Resource.Element` array) — the same bytes the
// fine-grained edit primitives already address — so one stored shape serves
// render, edit, and export at once (A-18-F2 dissolved without a second codec).
//
// Answer key (NF-1): a question carries `correct: ChoiceId`, a key pointing at
// ONE choice — NOT a boolean per choice. Zero-correct and two-correct become
// structurally unrepresentable, so the old `mcq_wrong_correct_count` counting
// Rule dissolves; what remains is a narrow referential Rule (`correct` names a
// choice that exists), enforced in `validate`.
//
// Pure module: no I/O, no clocks, no vendor SDK. Zod (via PqblTorus) is a pure
// runtime-type layer used only on the ingest boundary.
export { astForRender, contentForStudents, empty, forStudents, headingLevelOf, ingest, mkChoiceId, mkMediaType, mkNodeId, mkObjectiveId, mkPlainText, mkRichText, mkSkillId, nodeText, toContent, validate, };
import { exhaustSwitchCase, Result } from "@bilda/utils";
import * as Diagnostic from "../diagnostic.js";
import * as PqblTorus from "../template/pqblTorus.js";
import * as Content from "./content.js";
import * as Element from "./element.js";
import * as Reference from "./reference.js";
const mkNodeId = (s) => s;
const mkChoiceId = (s) => s;
const mkObjectiveId = (s) => s;
const mkSkillId = (s) => s;
const mkMediaType = (s) => s;
const mkPlainText = (s) => s.length === 0
    ? Result.err("PlainText must be non-empty")
    : Result.ok(s);
const mkRichText = (s) => s.length === 0
    ? Result.err("RichText must be non-empty")
    : Result.ok(s);
// Lenient readers: `Maybe`-shaped so the total, degrade-gracefully decoders
// (`treeToAst`, render) can drop a field that can't be a non-empty brand
// rather than fabricate one — keeping the `as`-cast confined to the smart ctor.
const plainOf = (s) => {
    if (s === undefined)
        return undefined;
    const r = mkPlainText(s);
    return r._tag === "Result.Ok" ? r.value : undefined;
};
const richOf = (s) => {
    if (s === undefined)
        return undefined;
    const r = mkRichText(s);
    return r._tag === "Result.Ok" ? r.value : undefined;
};
// The material as students meet it: every Note dropped, at any depth. Every
// reader that reaches students folds this, never the raw AST. Total over the
// node sum: a new kind stops it compiling until it says whether students see it.
const forStudents = (ast) => ({
    ...ast,
    body: studentNodes(ast.body),
});
const studentNodes = (nodes) => nodes.flatMap((node) => {
    switch (node.type) {
        case "section":
            return [{ ...node, children: studentNodes(node.children) }];
        case "heading":
        case "prose":
        case "list":
        case "question_single":
        case "media":
        case "include":
            return [node];
        case "note":
            return [];
        default:
            return exhaustSwitchCase(node);
    }
});
// The stored content as it may leave the app (a package, every export
// format): every Note element dropped, at any depth. Content that holds no
// Note passes byte for byte, its hash unchanged (a pqbl-torus object or plain
// text never holds one). The data-side twin of `forStudents`.
const contentForStudents = (content) => {
    const tree = Element.parseContentTree(content);
    return tree._tag === "Maybe.Just" && tree.value.some(holdsNote)
        ? Element.serializeContentTree(withoutNotes(tree.value))
        : content;
};
const isNote = (el) => el.type === "note";
const holdsNote = (el) => isNote(el) || (el.children ?? []).some(holdsNote);
const withoutNotes = (els) => els
    .filter((el) => !isNote(el))
    .map((el) => el.children === undefined
    ? el
    : { ...el, children: withoutNotes(el.children) });
const empty = { objectives: [], body: [] };
// === ingest — the create-boundary parser (STRICT) ===
//
// Runs ONCE, at create, mapping whatever the agent authored into the AST:
//   - not JSON            → plain-text prose (never fails).
//   - JSON with `course`  → pqbl-torus: PqblTorus.parse gates it, then
//                           `torusToAst` (fails on a question with no correct
//                           choice — the referential remnant of the dissolved
//                           count Rule).
//   - JSON array          → freeform element tree → AST; then `validate` gates.
//   - other JSON          → REFUSED (unrecognized shape) — this is the state a
//                           double-encoded / shape-drifted string used to land
//                           in and render as raw JSON.
const ingest = (raw) => {
    const text = Content.from(raw);
    const parsed = tryParseJson(text);
    if (parsed === undefined) {
        return Result.ok(textToAst(text));
    }
    if (isRecord(parsed) && "course" in parsed) {
        const r = PqblTorus.parse(parsed);
        if (r._tag === "Result.Err") {
            return Result.err(r.error.map(PqblTorus.describe));
        }
        return torusToAst(r.value);
    }
    if (Array.isArray(parsed)) {
        const tree = Element.parseContentTree(raw);
        if (tree._tag === "Maybe.Nothing") {
            return Result.err([
                Diagnostic.mkError("ast.malformed_tree", "content is a JSON array but not a well-formed element tree"),
            ]);
        }
        const ast = treeToAst(tree.value);
        const errs = validate(ast).filter((d) => d.severity === "error");
        return errs.length > 0 ? Result.err(errs) : Result.ok(ast);
    }
    return Result.err([
        Diagnostic.mkError("ast.unrecognized_shape", "content is JSON but not a recognized material shape (expected a pqbl-torus course object or an element array)"),
    ]);
};
// === astForRender — the stored-content reader (LENIENT / TOTAL) ===
//
// Render and read_material read the ONE stored shape (an element array) back
// into the AST; but they never error — an old/drifted material still shows
// SOMETHING. Falls through the same shapes as ingest, defaulting an
// unrecognized shape to a single prose block rather than refusing.
const astForRender = (content) => {
    const text = Content.from(content);
    const parsed = tryParseJson(text);
    if (parsed === undefined)
        return textToAst(text);
    if (Array.isArray(parsed)) {
        const tree = Element.parseContentTree(content);
        return tree._tag === "Maybe.Just"
            ? treeToAst(tree.value)
            : oneProse(text);
    }
    if (isRecord(parsed) && "course" in parsed) {
        const r = PqblTorus.parse(parsed);
        if (r._tag === "Result.Ok") {
            const ast = torusToAst(r.value);
            if (ast._tag === "Result.Ok")
                return ast.value;
        }
        return oneProse(text);
    }
    return oneProse(text);
};
// === toContent — serialize the AST to the stored element tree ===
//
// The inverse of `astForRender`'s array path. Produces the generic
// `Resource.Element` tree the edit primitives address, so a torus-authored
// material is edit-addressable exactly like a freeform one. `title` is dropped
// here — it lives on the Resource, not in content.
const toContent = (ast) => Element.serializeContentTree([
    ...ast.objectives.map(objectiveToElement),
    ...ast.body.map(nodeToElement),
]);
// The agent-facing read_material view IS the AST: brands erase to plain strings
// at runtime, so `JSON.stringify(ast)` already yields the documented
// `{title?, objectives, body:[typed nodes]}` shape (fixes A-18-F3's
// "read_material returns raw serialized JSON"). No separate projection needed —
// read_material serializes the parsed AST directly.
// === validate — the decide-time trust gate ===
//
// The AST type makes the SHAPE legal-by-construction; `validate` covers the
// cross-cutting invariants a fold can't (a log that folds is not necessarily
// correct). Returns `Diagnostic[]` so the same carrier reaches the editor and
// the agent self-repair loop. The counting Rule that once lived here
// (`mcq_wrong_correct_count`) is gone — `correct: ChoiceId` replaced it.
const validate = (ast) => {
    const out = [];
    // SkillId uniqueness across the objectives graph + the defined-skill set.
    const skillIds = new Set();
    for (const lo of ast.objectives) {
        if (lo.skills.length === 0) {
            out.push(Diagnostic.mkError("ast.objective_no_skills", `objective "${lo.label}" declares no skills`));
        }
        for (const s of lo.skills) {
            if (skillIds.has(`${s.id}`)) {
                out.push(Diagnostic.mkError("ast.duplicate_skill_id", `skill id "${s.id}" appears more than once`));
            }
            skillIds.add(`${s.id}`);
        }
    }
    // Question invariants + tested-skill referential integrity.
    const referenced = new Set();
    for (const q of allQuestions(ast.body)) {
        if (q.choices.length < 2) {
            out.push(Diagnostic.mkError("ast.too_few_choices", `question "${q.stem}" has fewer than 2 choices`));
        }
        if (!q.choices.some((c) => c.id === q.correct)) {
            out.push(Diagnostic.mkError("ast.correct_choice_missing", `question "${q.stem}" names a correct choice that does not exist`));
        }
        for (const skill of q.testedSkills) {
            referenced.add(`${skill}`);
            if (!skillIds.has(`${skill}`)) {
                out.push(Diagnostic.mkError("ast.skill_ref_unresolved", `question "${q.stem}" references unknown skill "${skill}"`));
            }
        }
    }
    // Uncovered skills — defined but never tested.
    for (const id of skillIds) {
        if (!referenced.has(id)) {
            out.push(Diagnostic.mkError("ast.skill_uncovered", `skill "${id}" is not tested by any question`));
        }
    }
    return out;
};
const allQuestions = function* (nodes) {
    for (const n of nodes) {
        if (n.type === "question_single")
            yield n;
        else if (n.type === "section")
            yield* allQuestions(n.children);
    }
};
// === torus ingest (PqblTorusContent → AST) ===
//
// `objectives → objectives`, `skill → skill`, `course.title → ast.title`,
// `module → SectionNode(L1)`, `page → SectionNode(L2)`, `mcq →
// SingleChoiceQuestionNode` (the ONE `correct:true` choice → `correct:
// ChoiceId`), `choice.feedback → Choice.feedback`, `testedSkills →
// testedSkills`. Fails when a mcq has no correct choice — the referential
// remnant of the dissolved count Rule (a mcq with two marked correct takes the
// first, single-choice being the MVP question type).
const torusToAst = (course) => {
    const objectives = course.objectives.objectives.flatMap(torusObjective);
    const modules = [];
    for (const mod of course.course.modules) {
        const pages = [];
        for (const page of mod.pages) {
            const questions = [];
            for (const mcq of page.mcqs) {
                const rQ = torusMcqToQuestion(mcq);
                if (rQ._tag === "Result.Err")
                    return rQ;
                questions.push(rQ.value);
            }
            const title = plainOf(`${page.title}`);
            if (title !== undefined) {
                pages.push({
                    type: "section",
                    id: mkNodeId(`${page.id}`),
                    title,
                    children: questions,
                });
            }
            else {
                pages.push(...questions);
            }
        }
        const title = plainOf(`${mod.title}`);
        if (title !== undefined) {
            modules.push({
                type: "section",
                id: mkNodeId(`${mod.id}`),
                title,
                children: pages,
            });
        }
        else {
            modules.push(...pages);
        }
    }
    const title = plainOf(`${course.course.title}`);
    return Result.ok({
        ...(title !== undefined ? { title } : {}),
        objectives,
        body: modules,
    });
};
const torusObjective = (lo) => {
    const label = plainOf(`${lo.label}`);
    if (label === undefined)
        return [];
    return [
        {
            id: mkObjectiveId(`${lo.id}`),
            label,
            skills: lo.skills.flatMap((s) => {
                const l = plainOf(`${s.label}`);
                return l === undefined
                    ? []
                    : [{ id: mkSkillId(`${s.id}`), label: l }];
            }),
        },
    ];
};
const torusMcqToQuestion = (mcq) => {
    const id = `${mcq.id}`;
    const stem = richOf(`${mcq.stem}`);
    if (stem === undefined) {
        return Result.err([
            Diagnostic.mkError("ast.malformed_question", "question has no stem"),
        ]);
    }
    const choices = [];
    for (const [i, c] of mcq.choices.entries()) {
        const text = richOf(`${c.text}`);
        if (text === undefined) {
            return Result.err([
                Diagnostic.mkError("ast.malformed_choice", `question "${mcq.stem}" has an empty choice`),
            ]);
        }
        const feedback = richOf(`${c.feedback}`);
        choices.push({
            id: mkChoiceId(`${id}::c${i}`),
            text,
            ...(feedback !== undefined ? { feedback } : {}),
        });
    }
    // The one `correct:true` choice → `correct: ChoiceId`; index aligns because
    // no choice was dropped above. No correct choice → the referential remnant
    // of the dissolved count Rule (two-marked would silently take the first).
    const correctIdx = mcq.choices.findIndex((c) => c.correct);
    const correct = correctIdx >= 0 ? choices[correctIdx] : undefined;
    if (correct === undefined) {
        return Result.err([
            Diagnostic.mkError("ast.correct_choice_missing", `question "${mcq.stem}" has no correct choice marked`),
        ]);
    }
    return Result.ok({
        type: "question_single",
        id: mkNodeId(id),
        stem,
        choices,
        correct: correct.id,
        testedSkills: mcq.testedSkills.map((s) => mkSkillId(`${s}`)),
    });
};
// === freeform element-tree ingest (Element[] → AST) ===
//
// Container kinds flatten (a titleless structural node inlines its children);
// a container WITH title text becomes a `SectionNode`. `objective` nodes are
// lifted (at any depth) into the objectives graph; `question`/`mcq` →
// `SingleChoiceQuestionNode`; `heading` → `HeadingNode`; a text leaf → prose.
const CONTAINER_KINDS = new Set([
    "course",
    "module",
    "quiz",
    "page",
    "section",
]);
const treeToAst = (tree) => {
    const objectives = [];
    const body = walkNodes(tree, objectives);
    return { objectives, body };
};
const walkNodes = (nodes, objAcc) => nodes.flatMap((n) => walkNode(n, objAcc));
const walkNode = (el, objAcc) => {
    const children = el.children ?? [];
    if (CONTAINER_KINDS.has(el.type)) {
        const title = plainOf(el.text);
        const inner = walkNodes(children, objAcc);
        if (title !== undefined) {
            return [
                {
                    type: "section",
                    id: mkNodeId(`${el.id}`),
                    title,
                    children: inner,
                },
            ];
        }
        return inner; // titleless structural node → flatten
    }
    switch (el.type) {
        case "objective": {
            objAcc.push(...elementToObjective(el));
            return [];
        }
        case "question":
        case "mcq":
            return elementToQuestion(el);
        case Reference.INCLUDE_ELEMENT_TYPE: {
            // A malformed include degrades to nothing (lenient reader).
            const target = Reference.includeTargetOf(el);
            return target._tag === "Maybe.Just"
                ? [
                    {
                        type: "include",
                        id: mkNodeId(`${el.id}`),
                        target: target.value,
                    },
                ]
                : [];
        }
        // A note keeps its kind: read as prose, it would reach students.
        case "note": {
            const text = richOf(collectText(el));
            return text === undefined
                ? []
                : [{ type: "note", id: mkNodeId(`${el.id}`), text }];
        }
        case "heading": {
            const text = richOf(collectText(el));
            return text === undefined
                ? []
                : [
                    {
                        type: "heading",
                        id: mkNodeId(`${el.id}`),
                        level: levelOf(el),
                        text,
                    },
                ];
        }
        default: {
            if (children.length > 0)
                return walkNodes(children, objAcc);
            const text = richOf(collectText(el));
            return text === undefined
                ? []
                : [{ type: "prose", id: mkNodeId(`${el.id}`), text }];
        }
    }
};
const elementToObjective = (el) => {
    const label = plainOf(el.text ?? collectText(el));
    if (label === undefined)
        return [];
    return [
        {
            id: mkObjectiveId(`${el.id}`),
            label,
            skills: (el.children ?? [])
                .filter((c) => c.type === "skill")
                .flatMap((c) => {
                const l = plainOf(c.text ?? "");
                return l === undefined
                    ? []
                    : [{ id: mkSkillId(`${c.id}`), label: l }];
            }),
        },
    ];
};
// Lenient (drifted content): a question with no usable stem is dropped whole
// rather than fabricating one. Each option keeps its parallel `correct` flag so
// the single answer key resolves without index fragility after empty-text drops.
const elementToQuestion = (el) => {
    const stem = richOf(el.text ?? "");
    if (stem === undefined)
        return [];
    const options = (el.children ?? []).filter((c) => c.type === "option");
    const built = options.flatMap((o, i) => {
        const text = richOf(o.text ?? "");
        if (text === undefined)
            return [];
        const feedback = richOf(feedbackOf(o));
        const choice = {
            id: mkChoiceId(o.id.length > 0 ? `${o.id}` : `${el.id}::c${i}`),
            text,
            ...(feedback !== undefined ? { feedback } : {}),
        };
        return [{ choice, correct: correctOf(o) }];
    });
    const correctEntry = built.find((b) => b.correct) ?? built[0];
    const feedback = richOf(questionFeedbackOf(el));
    return [
        {
            type: "question_single",
            id: mkNodeId(`${el.id}`),
            stem,
            choices: built.map((b) => b.choice),
            correct: correctEntry?.choice.id ?? mkChoiceId(""),
            testedSkills: testedSkillsOf(el),
            ...(feedback !== undefined ? { feedback } : {}),
        },
    ];
};
// === element-tree serialize helpers (AST node → Element) ===
//
// The inverse of `walkNode`. Questions serialize with the answer key on each
// option (`attrs.correct: boolean`, exactly one true) — the convention the
// existing edit primitives and the UI adapter read — even though the AST holds
// the single `correct: ChoiceId`; the round-trip resolves the boolean back to
// the one key on read.
const objectiveToElement = (lo) => ({
    id: Element.mkElementId(`${lo.id}`),
    type: "objective",
    text: `${lo.label}`,
    children: lo.skills.map((s) => ({
        id: Element.mkElementId(`${s.id}`),
        type: "skill",
        text: `${s.label}`,
    })),
});
const nodeToElement = (node) => {
    switch (node.type) {
        case "section":
            return {
                id: Element.mkElementId(`${node.id}`),
                type: "section",
                text: `${node.title}`,
                children: node.children.map(nodeToElement),
            };
        case "heading":
            return {
                id: Element.mkElementId(`${node.id}`),
                type: "heading",
                text: `${node.text}`,
                attrs: { level: node.level },
            };
        case "prose":
            return {
                id: Element.mkElementId(`${node.id}`),
                type: "prose",
                text: `${node.text}`,
            };
        case "list":
            return {
                id: Element.mkElementId(`${node.id}`),
                type: "list",
                attrs: {
                    ordering: node.ordering,
                    items: node.items.map((i) => `${i}`),
                },
            };
        case "question_single":
            return {
                id: Element.mkElementId(`${node.id}`),
                type: "question",
                text: `${node.stem}`,
                attrs: {
                    testedSkills: node.testedSkills.map((s) => `${s}`),
                    ...(node.feedback !== undefined
                        ? { feedback: `${node.feedback}` }
                        : {}),
                },
                children: node.choices.map((c) => ({
                    id: Element.mkElementId(`${c.id}`),
                    type: "option",
                    text: `${c.text}`,
                    attrs: {
                        correct: c.id === node.correct,
                        ...(c.feedback !== undefined
                            ? { feedback: `${c.feedback}` }
                            : {}),
                    },
                })),
            };
        case "media":
            return {
                id: Element.mkElementId(`${node.id}`),
                type: "media",
                attrs: {
                    mediaType: `${node.ref.mediaType}`,
                    alt: `${node.ref.alt}`,
                    ...(node.caption !== undefined
                        ? { caption: `${node.caption}` }
                        : {}),
                },
            };
        case "include":
            return Reference.mkIncludeElement(Element.mkElementId(`${node.id}`), node.target);
        case "note":
            return {
                id: Element.mkElementId(`${node.id}`),
                type: "note",
                text: `${node.text}`,
            };
        default:
            return exhaustSwitchCase(node);
    }
};
// === plain-text ingest ===
const textToAst = (text) => {
    const trimmed = text.trim();
    if (trimmed.length === 0)
        return empty;
    const body = [];
    trimmed.split(/\n{2,}/).forEach((p, i) => {
        const t = richOf(p.trim());
        if (t !== undefined) {
            body.push({ type: "prose", id: mkNodeId(`p-${i}`), text: t });
        }
    });
    return { objectives: [], body };
};
const oneProse = (text) => {
    const t = richOf(text);
    return t === undefined
        ? empty
        : {
            objectives: [],
            body: [{ type: "prose", id: mkNodeId("content"), text: t }],
        };
};
// === low-level readers over the untyped element tree ===
const collectText = (el) => {
    if (el.text !== undefined)
        return el.text;
    if (el.children === undefined)
        return "";
    return el.children.map(collectText).join(" ");
};
// A heading level read off untrusted data: 1-6, else 2 (the neutral
// default). Total.
const headingLevelOf = (raw) => raw === 1 || raw === 2 || raw === 3 || raw === 4 || raw === 5 || raw === 6
    ? raw
    : 2;
const levelOf = (el) => headingLevelOf(el.attrs?.level);
const correctOf = (el) => el.attrs?.correct === true;
const feedbackOf = (el) => {
    const fromAttrs = el.attrs?.feedback;
    return typeof fromAttrs === "string" ? fromAttrs : "";
};
// A question's own response: its stored `attrs.feedback`, else the quiz
// shape's `feedback` child as authored (paragraphs joined).
const questionFeedbackOf = (el) => feedbackOf(el) ||
    (el.children ?? [])
        .filter((c) => c.type === "feedback")
        .flatMap((fb) => fb.children?.map(collectText) ?? [fb.text ?? ""])
        .filter((t) => t.length > 0)
        .join("\n\n");
const testedSkillsOf = (el) => {
    const raw = el.attrs?.testedSkills;
    return Array.isArray(raw)
        ? raw
            .filter((s) => typeof s === "string")
            .map((s) => mkSkillId(s))
        : [];
};
// A node's readable text, in document order: titles, prose, list items,
// question stems, choices + feedback, media alt/caption — never ids or JSON
// keys. Sections recurse; an include carries no text of its own. A note is
// not material text (students never read it), so the checks that judge the
// material's text never see it.
const nodeText = (node) => {
    switch (node.type) {
        case "section":
            return [node.title, ...node.children.flatMap(nodeText)];
        case "heading":
        case "prose":
            return [node.text];
        case "list":
            return node.items;
        case "question_single":
            return [
                node.stem,
                ...node.choices.flatMap((c) => c.feedback === undefined ? [c.text] : [c.text, c.feedback]),
                ...(node.feedback === undefined ? [] : [node.feedback]),
            ];
        case "media":
            return node.caption === undefined
                ? [node.ref.alt]
                : [node.ref.alt, node.caption];
        case "include":
        case "note":
            return [];
        default:
            return exhaustSwitchCase(node);
    }
};
const tryParseJson = (raw) => {
    try {
        return JSON.parse(raw);
    }
    catch {
        return undefined;
    }
};
const isRecord = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
//# sourceMappingURL=ast.js.map